import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import { buildAutoDistributeItems, buildManualDistributeItems } from '@/lib/layout/auto-distribute'
import type { SpaceDirection } from '../types/layout-operations'
import type { LiveSelection } from './live-selection'

/**
 * Position moves for Space Between: 'auto' equalizes the gaps (3+ objects), 'manual' applies a
 * fixed `gap` (2+ objects). `baseline` was never passed a value by any caller, so it's dropped
 * rather than threaded through dead.
 */
export function buildDistributionItems(
  { allShapes, selectedShapes }: LiveSelection,
  getBounds: (id: string) => ShapeBounds | undefined,
  mode: 'auto' | 'manual',
  direction: SpaceDirection,
  gap?: number
): BatchShapeTransformItem[] {
  const minCount = mode === 'auto' ? 3 : 2

  if (selectedShapes.length < minCount) {
    return []
  }

  const shapeMap = new Map(selectedShapes.map((shape) => [shape.id, shape] as const))
  const layerOrder = allShapes.map((shape) => shape.id)
  const items =
    mode === 'auto'
      ? buildAutoDistributeItems(
          selectedShapes,
          getBounds,
          direction === 'horizontal' ? 'x' : 'y',
          layerOrder
        )
      : buildManualDistributeItems(
          selectedShapes,
          getBounds,
          direction === 'horizontal' ? 'x' : 'y',
          layerOrder,
          gap ?? 0
        )

  const commitItems: BatchShapeTransformItem[] = []

  for (const item of items) {
    const shape = shapeMap.get(item.shapeId)

    if (!shape) {
      continue
    }

    commitItems.push({
      shapeId: item.shapeId,
      before: { x: shape.x, y: shape.y },
      after: { x: item.after.x ?? shape.x, y: item.after.y ?? shape.y },
    })
  }

  return commitItems
}
